import * as React from 'react';

import { Button, Menu } from '@rematter/ui-web';
import {
  LucideEllipsis,
  LucidePowerOff,
  LucideSend,
  LucideSquarePen,
  LucideTrash2,
  LucideUserPlus,
} from 'lucide-react';

import type { UserRecord } from './types';

import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { usePermission } from '~/shared/hooks/usePermission';

type UserRowMenuProps = {
  user: UserRecord;
  onEdit: () => void;
  onResendInvite: () => void;
  onRemoveAccess: () => void;
  onRestoreAccess: () => void;
  onDelete: () => void;
  isDisabled?: boolean;
};

export const UserRowMenu = ({
  user,
  onEdit,
  onResendInvite,
  onRemoveAccess,
  onRestoreAccess,
  onDelete,
  isDisabled,
}: UserRowMenuProps) => {
  const t = useTranslations();
  const { allowed: canUpdate } = usePermission('userUpdate');
  const { allowed: canDelete } = usePermission('userDelete');

  const menuItems = React.useMemo(() => {
    const items: Array<{ label: React.ReactNode; onAction: () => void }> = [];

    items.push({
      label: (
        <div className="flex items-center gap-2">
          <LucideSquarePen className="size-4" />
          {t.teamAndPermissions.users.actions.edit}
        </div>
      ),
      onAction: onEdit,
    });

    if (user.status === 'invite_sent') {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <LucideSend className="size-4" />
            {t.teamAndPermissions.users.actions.resendInvite}
          </div>
        ),
        onAction: onResendInvite,
      });
    }

    if (canUpdate) {
      if (user.status === 'archived') {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              <LucideUserPlus className="size-4" />
              {t.teamAndPermissions.users.actions.restoreAccess}
            </div>
          ),
          onAction: onRestoreAccess,
        });
      } else {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              <LucidePowerOff className="size-4" />
              {t.teamAndPermissions.users.actions.removeAccess}
            </div>
          ),
          onAction: onRemoveAccess,
        });
      }
    }

    if (canDelete) {
      items.push({
        label: (
          <div className="flex items-center gap-2 text-failure">
            <LucideTrash2 className="size-4" />
            {t.teamAndPermissions.users.actions.deleteUser}
          </div>
        ),
        onAction: onDelete,
      });
    }

    return items;
  }, [
    user.status,
    t,
    canUpdate,
    canDelete,
    onEdit,
    onResendInvite,
    onRemoveAccess,
    onRestoreAccess,
    onDelete,
  ]);

  return (
    <Menu
      trigger={
        <Button
          size="icon"
          icon={<LucideEllipsis />}
          variant="outline"
          color="neutral"
          isDisabled={isDisabled}
        />
      }
      items={menuItems}
      menuClassName="@UserRowMenu"
    />
  );
};
